/* @flow */
import * as React from 'react';

import { Box, Button, Flex, Footer, Icon, Space, Text } from '@sonyged/proc-ui';
import css from '@styled-system/css';
import styled from 'styled-components';

import type { Customer, Sushi } from '../rules/types';
import { CustomerDataPill } from './CustomerDataPill';
import { Select } from './Select';
import { SushiContext } from '../SushiContext';
import SushiMenu from './SushiMenu';

const NUMBERS = ['⓪', '①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩'];

type Props = {
  setting: Array<?Sushi>,
  customerGroup: Array<{ ...Customer, eatenCount: number }>,
  onChange: ([Sushi, number]) => void,
  onStart: Function,
  onBack: Function,
  showCustomerStat: Customer => void,
  half: 'first' | 'second',
  roundIndex: number,
  moreKanji?: boolean,
};

export function SushiSelectorScreen(props: Props) {
  const { setting, customerGroup, onChange, onStart, onBack, showCustomerStat, moreKanji, half } = props;

  const mapper = React.useContext(SushiContext);

  const erabu = moreKanji ? '選ぶ' : 'えらぶ';

  let menuText;
  if (half === 'first') {
    menuText = '組目 前半';
  }

  if (half === 'second') {
    menuText = '組目 後半';
  }

  return (
    <>
      <SushiMenu
        exitLink={mapper.getExitLink()}
        query={'?skip_intro=true'}
        title={''}
        subTitle={mapper.getTitle()}
        menuText={props.roundIndex + 1 + menuText}
      />
      <Flex
        paddingBottom={['8px', null, '12px']}
        alignItems="flex-end"
        justifyContent="center"
        height={[110, 110, 120]}
        bg="tertiary100"
      >
        <Space mx={['4px', null, '8px']}>
          {customerGroup.map((customer, i) => (
            <Flex key={i} flexDirection="column" alignItems="center">
              <CustomerDataPill
                disabled={customer.eatenCount === 5}
                moreKanji={moreKanji}
                customer={customer}
                onClick={showCustomerStat}
              >
                {moreKanji ? '調べる' : 'しらべる'}
              </CustomerDataPill>
              <Text fontSize={[14, 14, 18]} color="#808080">
                {customer.eatenCount === 5
                  ? 'おしまい'
                  : (customer.eatenCount + 1).toString(10) + (moreKanji ? '皿目' : 'さら目')}
              </Text>
            </Flex>
          ))}
        </Space>
      </Flex>

      <Box flex={1} minHeight={0} bg="gray10" style={{ overflow: 'auto' }}>
        <Space my={[0, 0, 48]}>
          <SettingPanel>
            <Space mt={[24, 24, 36]}>
              <Text color="gray100" fontSize={[18, 18, 22]} lineHeight={1}>
                {moreKanji ? 'おすしのネタを選ぼう' : 'おすしのネタをえらぼう'}
              </Text>
            </Space>
            <Space my={[12, 12, 24]}>
              <Text color="gray100" fontSize={[14, 14, 17]} lineHeight={1}>
                {moreKanji ? '「調べる」を見て流すおすしを選ぼう' : '「しらべる」を見てながすおすしをえらぼう'}
              </Text>
            </Space>

            <Space mb={[16, 16, 32]}>
              <Flex justifyContent="center" alignSelf="stretch">
                <Space mx={['3px', null, null]}>
                  {new Array(customerGroup.length > 1 ? (customerGroup.length > 2 ? 8 : 6) : 4).fill(0).map((_, i) => (
                    <SelectedItem key={i} iconSrc={setting[i] && mapper.getSushiBwIcon(setting[i])} />
                  ))}
                </Space>
              </Flex>
            </Space>

            <Space mb={[16, 16, 32]}>
              <SelectorGroup>
                <Flex>
                  <Select
                    blankLabel={erabu + NUMBERS[1]}
                    moreKanji={moreKanji}
                    flex={1}
                    value={setting[0]}
                    onChange={sushi => onChange([sushi, 0])}
                  />
                  <Space ml={['24px', null, '32px']}>
                    <Select
                      blankLabel={erabu + NUMBERS[2]}
                      flex={1}
                      value={setting[1]}
                      moreKanji={moreKanji}
                      onChange={sushi => onChange([sushi, 1])}
                    />
                  </Space>
                </Flex>
                <Space marginTop={['12px', null, '16px']}>
                  {new Array(customerGroup.length > 1 ? (customerGroup.length > 2 ? 3 : 2) : 1).fill(0).map((_, i) => (
                    <Flex key={i}>
                      <Select
                        blankLabel={erabu + NUMBERS[3 + i * 2]}
                        flex={1}
                        value={setting[(i + 1) * 2]}
                        moreKanji={moreKanji}
                        onChange={sushi => onChange([sushi, (i + 1) * 2])}
                      />
                      <Space ml={['24px', null, '32px']}>
                        <Select
                          blankLabel={erabu + NUMBERS[4 + i * 2]}
                          flex={1}
                          value={setting[(i + 1) * 2 + 1]}
                          moreKanji={moreKanji}
                          onChange={sushi => onChange([sushi, (i + 1) * 2 + 1])}
                        />
                      </Space>
                    </Flex>
                  ))}
                </Space>
              </SelectorGroup>
            </Space>
          </SettingPanel>
        </Space>
      </Box>

      <Footer>
        <Footer.ButtonGroup>
          <Button variant="outline" onClick={onBack}>
            {half === 'second' ? moreKanji ? '前半の結果' : '前半のけっか' : <Icon variant="back" />}
          </Button>
        </Footer.ButtonGroup>

        <Footer.ActionButton disabled={setting.every(x => x === null)} variant="attention" onClick={onStart}>
          <Icon variant="action" />
        </Footer.ActionButton>
      </Footer>
    </>
  );
}

const SettingPanel = styled(Flex)(
  css({
    borderRadius: '15px',
    mx: 'auto',
    width: [null, null, '800px'],
    bg: [null, null, 'white'],
    flexDirection: 'column',
    alignItems: 'center',
    px: [16, 16, 32],
  })
);

const SelectedItem = (props: { iconSrc: ?string }) => {
  const { iconSrc, ...rest } = props;
  return (
    <SelectedItemContainer {...rest}>
      {iconSrc && <SelectedItemIcon src={iconSrc} alt="sushi icon" />}
    </SelectedItemContainer>
  );
};

const SelectedItemContainer = styled(Box)(
  css({
    position: 'relative',
    borderRadius: '100%',
    maxWidth: ['38px', null, '58px'],
    display: 'inline-block',
    bg: ['white', null, 'gray10'],
    flex: 1,

    '::before': {
      content: '""',
      display: 'block',
      paddingBottom: '100%', // maintain 1x1 aspect ratio
      height: 0,
    },
  })
);

const SelectedItemIcon = styled('img')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: 'auto',
});

const SelectorGroup = styled(Box)(
  css({
    borderStyle: 'solid',
    borderWidth: ['1px', null, '2px'],
    borderRadius: ['8px', null, '12px'],
    borderColor: 'secondary30',
    maxWidth: ['440px', null, '540px'],
    bg: 'white',
    width: '100%',
    px: ['12px', null, '16px'],
    py: ['12px', null, '26px'],
  })
);
